import React, { useMemo, ReactNode } from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import GridData from '../../../../components/GridData/GridData';
import { GetMessageErrorReportsResponse, MessageErrorReportDetail } from '../../types';
import { GetMessageErrorSummaryReportsResponse, MessageErrorReportSummary } from '../../api/types';
import { GroupBy } from '../../controllers/ReportsViewController';

// Extended type for GridData rows (includes required id field)
type ReportsTableRow = MessageErrorReportDetail & { id: string };

interface ReportsTableProps {
  messageErrorReports: GetMessageErrorReportsResponse | null;
  messageErrorSummaryReports: GetMessageErrorSummaryReportsResponse | null;
  groupBy: GroupBy;
  sortConfig?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortChange?: (sortModel: GridSortModel) => void;
  onDownloadRow?: (row: MessageErrorReportDetail) => void;
  isLoading?: boolean;
  headerContent?: ReactNode;
}

export const ReportsTable: React.FC<ReportsTableProps> = ({
  messageErrorReports,
  messageErrorSummaryReports,
  groupBy,
  sortConfig,
  onSortChange,
  onDownloadRow,
  isLoading = false,
  headerContent,
}) => {
  const details = messageErrorReports || [];
  const summaryData = messageErrorSummaryReports || [];

  // Create mapping from errorCode to error description
  const errorCodeMapping = useMemo(() => {
    const mapping: Record<string, { title: string; detail: string }> = {};
    summaryData.forEach((summary: MessageErrorReportSummary) => {
      if (summary.errorCode) {
        mapping[summary.errorCode] = {
          title: summary.errorTitle,
          detail: summary.errorDetail,
        };
      }
    });
    return mapping;
  }, [summaryData]);

  // Transform data to include required id field for GridData
  const rows: ReportsTableRow[] = useMemo(() => {
    return details.map((detail, index) => ({
      ...detail,
      id: `${detail.application?.id}-${detail.company?.id}-${detail.errorCode || 'unknown'}-${
        detail.intervalStart
      }-${index}`,
    }));
  }, [details]);

  // Define columns for GridData
  const columns: GridColDef<ReportsTableRow>[] = useMemo(
    () => [
      {
        field: 'applicationName',
        headerName: 'Application',
        sortConfig: { field: 'application_name' },
        minWidth: 250,
        flex: 2,
        valueGetter: (value, row) => row.application?.name,
        renderCell: params => {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">{params.row.application?.name}</Typography>
            </Box>
          );
        },
        initVisibility: groupBy === 'application',
      },
      {
        field: 'companyName',
        headerName: 'Company',
        sortConfig: { field: 'company_name' },
        minWidth: 250,
        flex: 2,
        valueGetter: (value, row) => row.company?.name,
        renderCell: params => {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">{params.row.company?.name}</Typography>
            </Box>
          );
        },
        initVisibility: groupBy === 'company',
      },
      {
        field: 'errorCode',
        headerName: 'Error Code',
        sortConfig: { field: 'error_code' },
        minWidth: 120,
        flex: 1,
        sortable: true,
        renderCell: params => {
          const errorCode = params.value as string | null;
          const errorInfo = errorCode ? errorCodeMapping[errorCode] : null;

          return (
            <Box display="flex" alignItems="center" height="100%">
              {errorInfo ? (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" component="div" fontWeight="bold">
                        {errorInfo.title}
                      </Typography>
                      <Typography variant="body2" component="div">
                        {errorInfo.detail}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Typography variant="body2">
                    {errorCode || 'Unknown'}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2">{errorCode || 'Unknown'}</Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: 'errorCount',
        headerName: 'Error Count',
        type: 'number',
        minWidth: 120,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: params => (
          <Box display="flex" alignItems="center" height="100%">
            {parseInt(params.value).toLocaleString()}
          </Box>
        ),
        sortable: true,
      },
      {
        field: 'totalMessages',
        headerName: 'Total Messages',
        type: 'number',
        minWidth: 140,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: params => (
          <Box display="flex" alignItems="center" height="100%">
            {parseInt(params.value).toLocaleString()}
          </Box>
        ),
        sortable: true,
      },
      {
        field: 'errorRate',
        headerName: 'Error Rate',
        type: 'number',
        minWidth: 100,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        sortable: false,
        valueGetter: (value, row) => {
          return ((row.errorCount / row.totalMessages) * 100).toFixed(2);
        },
        renderCell: params => {
          const errorRate = parseFloat(params.value as string);
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2" color={errorRate > 10 ? 'error' : 'inherit'}>
                {errorRate}%
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'timePeriod',
        headerName: 'Time Period',
        minWidth: 200,
        flex: 1.5,
        sortable: true,
        initVisibility: false,
        valueGetter: (value, row) => row.intervalStart,
        renderCell: params => (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">
              {new Date(params.row.intervalStart).toLocaleDateString()} -{' '}
              {new Date(params.row.intervalEnd).toLocaleDateString()}
            </Typography>
          </Box>
        ),
      },
    ],
    [groupBy, errorCodeMapping],
  );

  // Convert sortConfig to GridSortModel format
  const sortModel: GridSortModel = useMemo(() => {
    if (!sortConfig) return [];

    // Map the field names to match the column fields
    let field = sortConfig.field;
    if (field === 'application_name') field = 'applicationName';
    if (field === 'company_name') field = 'companyName';
    if (field === 'error_code') field = 'errorCode';
    if (field === 'error_count') field = 'errorCount';
    if (field === 'total_messages') field = 'totalMessages';
    if (field === 'interval_start') field = 'timePeriod';

    return [{ field, sort: sortConfig.direction }];
  }, [sortConfig]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (onSortChange) {
      onSortChange(newSortModel);
    }
  };

  return (
    <GridData
      key={`reports-table-${groupBy}`} // to force re-render when groupBy changes
      header={headerContent}
      isLoading={isLoading}
      rows={rows}
      columns={columns}
      sorting={{
        value: sortModel,
        onChange: handleSortModelChange,
      }}
      pagination={null}
      enableColumnFiltering={true}
      renderAction={
        onDownloadRow
          ? row => (
              <Tooltip title="Download error messages">
                <span>
                  <IconButton size="small" color="primary" onClick={() => onDownloadRow(row)}>
                    <DownloadIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            )
          : undefined
      }
    />
  );
};
